/**
 * Translate a JSON-Pointer-like validation error path emitted by the
 * backend (TASK-176 — `errors[].path`) into a `VisualLocator` that points
 * the visual editor at a specific tab/field/subfield/component.
 *
 * Two path styles are seen in the wild:
 *   - Dot-separated: `tabs.0.fields.3.subfields.1.required`
 *   - Slash-separated (RFC 6901 JSON Pointer): `/tabs/0/fields/3/subfields/1/required`
 *   - Bracket-indexed: `tabs[0].fields[3].subfields[1].required`
 *
 * This utility normalises all three. Anything that can't be parsed gets a
 * pass-through `propertyKey` so the UI still shows the path in a generic
 * "Other errors" panel.
 *
 * Tests in `errorPathToVisualLocator.test.ts` lock the 5+ scenarios from
 * the implementation plan (CP-1).
 */
var tokenize = function (path) {
    if (!path)
        return [];
    // Replace `[N]` bracket indices with `.N`, then split on both `.` and `/`.
    var flattened = path
        .replace(/\[(\d+)\]/g, '.$1')
        .replace(/^[./]/, '');
    return flattened.split(/[./]+/).filter(function (t) { return t.length > 0; });
};
var consumeIndex = function (tokens) {
    if (tokens.length === 0)
        return null;
    var head = tokens[0];
    if (/^\d+$/.test(head)) {
        tokens.shift();
        return Number(head);
    }
    return null;
};
/**
 * Read `tokens[0]` as a plain `string`. Without the explicit cast, TS
 * control-flow analysis narrows the array element type to a literal
 * after each `!==` check upstream, then complains the next comparison
 * has no overlap — even though `tokens.shift()` already mutated the
 * array. The cast resets the inferred type.
 */
var peek = function (tokens) { return tokens[0]; };
var remainderToPropertyKey = function (tokens) {
    if (tokens.length === 0)
        return undefined;
    // Re-stitch with `.` so `options.2.value` becomes `options.2.value`
    // (which is human-readable enough for the property panel display).
    return tokens.join('.');
};
export var errorPathToVisualLocator = function (path) {
    var locator = { rawPath: path };
    var tokens = tokenize(path);
    if (tokens.length === 0) {
        return locator;
    }
    // First segment MUST be `tabs` for us to drill in. Anything else is a
    // root-level config error (e.g. `tabs` itself, top-level metadata)
    // and gets a pass-through propertyKey.
    if (peek(tokens) !== 'tabs') {
        locator.propertyKey = remainderToPropertyKey(tokens);
        return locator;
    }
    tokens.shift(); // consume 'tabs'
    var tabIdx = consumeIndex(tokens);
    if (tabIdx === null) {
        // `tabs` without an index — root-level error on the array itself.
        locator.propertyKey = 'tabs';
        return locator;
    }
    locator.tabIdx = tabIdx;
    if (tokens.length === 0)
        return locator;
    // Cast to string explicitly: TS narrows `tokens[0]` to the literal
    // `'tabs'` after the previous mismatch branch above, even though
    // `tokens.shift()` mutated the array. Without the cast TS flags the
    // comparison as never-true.
    if (peek(tokens) !== 'fields') {
        // e.g. `tabs.0.label` → tabIdx=0, propertyKey='label'
        locator.propertyKey = remainderToPropertyKey(tokens);
        return locator;
    }
    tokens.shift(); // consume 'fields'
    var fieldIdx = consumeIndex(tokens);
    if (fieldIdx === null) {
        locator.propertyKey = 'fields';
        return locator;
    }
    locator.fieldIdx = fieldIdx;
    if (tokens.length === 0)
        return locator;
    var next = peek(tokens);
    if (next === 'subfields') {
        tokens.shift();
        var subfieldIdx = consumeIndex(tokens);
        if (subfieldIdx === null) {
            locator.propertyKey = 'subfields';
            return locator;
        }
        locator.subfieldIdx = subfieldIdx;
        if (tokens.length === 0)
            return locator;
        if (peek(tokens) === 'components') {
            tokens.shift();
            var componentIdx = consumeIndex(tokens);
            if (componentIdx === null) {
                locator.propertyKey = 'components';
                return locator;
            }
            locator.componentIdx = componentIdx;
            locator.propertyKey = remainderToPropertyKey(tokens);
            return locator;
        }
        locator.propertyKey = remainderToPropertyKey(tokens);
        return locator;
    }
    if (next === 'indicator1Def') {
        tokens.shift();
        locator.indicator = 1;
        locator.propertyKey = remainderToPropertyKey(tokens);
        return locator;
    }
    if (next === 'indicator2Def') {
        tokens.shift();
        locator.indicator = 2;
        locator.propertyKey = remainderToPropertyKey(tokens);
        return locator;
    }
    locator.propertyKey = remainderToPropertyKey(tokens);
    return locator;
};
/**
 * Return true if the given locator points at the same field as the given
 * (tabIdx, fieldIdx). Used by `FieldPropertyPanel` to filter validation
 * errors that should render inline on the currently-edited field.
 */
export var locatorMatchesField = function (locator, tabIdx, fieldIdx) { return locator.tabIdx === tabIdx && locator.fieldIdx === fieldIdx; };
